import { useCallback, useEffect, useState } from "react";
import posthog from "posthog-js";
import {
  CONSENT_CHANGED_EVENT,
  ConsentCategories,
  ConsentRecord,
  allConsentGranted,
  getConsent,
  hasAnalyticsConsent,
  initVisitorTracking,
  necessaryOnlyConsent,
  saveConsent,
} from "../visitorTracking";

// Other parts of the app (e.g. the Privacy page footer) can reopen the
// preferences dialog by dispatching this event.
export const OPEN_COOKIE_PREFERENCES_EVENT = "kono-open-cookie-preferences";

type Category = {
  key: keyof ConsentCategories;
  title: string;
  description: string;
  locked?: boolean;
};

const CATEGORIES: Category[] = [
  {
    key: "necessary",
    title: "Strictly necessary",
    description:
      "Required for the site to work — sign-in, security, your cart and checkout. These can’t be switched off.",
    locked: true,
  },
  {
    key: "functional",
    title: "Functional",
    description:
      "Remember your preferences and choices (like recent searches and saved items) to give you a better experience.",
  },
  {
    key: "analytics",
    title: "Analytics",
    description:
      "Help us understand how visitors use the site — pages viewed, searches, and journeys — so we can improve it.",
  },
  {
    key: "marketing",
    title: "Marketing",
    description:
      "Let us tailor and measure communications and offers that may be relevant to you.",
  },
];

/** Apply a consent decision to third-party analytics (PostHog opt in/out). */
const applyToPostHog = (analytics: boolean) => {
  try {
    if (analytics) {
      posthog.opt_in_capturing?.();
    } else {
      posthog.opt_out_capturing?.();
    }
  } catch {
    /* posthog may not be initialized in some envs */
  }
};

export const CookieConsent = () => {
  const existing = getConsent();
  const [visible, setVisible] = useState<boolean>(!existing);
  const [customizing, setCustomizing] = useState(false);
  // Default ALL categories selected (opt-out model, as requested).
  const [selection, setSelection] = useState<ConsentCategories>(
    existing
      ? {
          necessary: true,
          functional: existing.functional,
          analytics: existing.analytics,
          marketing: existing.marketing,
        }
      : allConsentGranted(),
  );

  // On first mount, reflect the stored decision into PostHog + tracking.
  useEffect(() => {
    const consent = getConsent();
    applyToPostHog(consent?.analytics === true);
    if (consent?.analytics) initVisitorTracking();
  }, []);

  // Allow the app to reopen the preferences dialog on demand.
  useEffect(() => {
    const open = () => {
      const c = getConsent();
      if (c) {
        setSelection({
          necessary: true,
          functional: c.functional,
          analytics: c.analytics,
          marketing: c.marketing,
        });
      }
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
    return () =>
      window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
  }, []);

  const commit = useCallback((categories: ConsentCategories) => {
    const record: ConsentRecord = saveConsent(categories);
    applyToPostHog(record.analytics);
    if (record.analytics && !hasAnalyticsConsent()) {
      // no-op safety; saveConsent already persisted
    }
    if (record.analytics) initVisitorTracking();
    setVisible(false);
    setCustomizing(false);
  }, []);

  if (!visible) return null;

  return (
    // The wrapper spans the full width but the card inside is centred and
    // narrower, so the rest of the strip is transparent. It still sat on top of
    // whatever was in the bottom corners and swallowed their clicks, so it takes
    // no pointer events and the card takes them back.
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-6 sm:pb-6">
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white p-4 shadow-2xl sm:p-6">
        {!customizing ? (
          <>
            <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
              We value your privacy
            </h2>
            <p className="mt-1 text-xs leading-5 text-gray-600 sm:text-sm">
              We use cookies to run the site, understand how it’s used, and
              improve your experience. You can accept all cookies, keep only the
              strictly necessary ones, or choose which categories to allow. See
              our{" "}
              <a href="/privacy" className="underline hover:text-black">
                Privacy Policy
              </a>
              .
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => setCustomizing(true)}
                className="order-3 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:order-1"
              >
                Customize
              </button>
              <button
                onClick={() => commit(necessaryOnlyConsent())}
                className="order-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Only necessary
              </button>
              <button
                onClick={() => commit(allConsentGranted())}
                className="order-1 rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 sm:order-3"
              >
                Accept all
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
              Cookie preferences
            </h2>
            <p className="mt-1 text-xs leading-5 text-gray-600">
              All categories are on by default. Turn off any you’d rather not
              allow, then save.
            </p>
            <div className="mt-4 max-h-[45vh] space-y-3 overflow-y-auto pr-1">
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.key}
                  className="flex items-start gap-3 rounded-lg border border-gray-200 p-3"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-black"
                    checked={cat.locked ? true : selection[cat.key]}
                    disabled={cat.locked}
                    onChange={(e) =>
                      setSelection((s) => ({
                        ...s,
                        [cat.key]: e.target.checked,
                      }))
                    }
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      {cat.title}
                      {cat.locked && (
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-gray-500">
                          Always on
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-gray-600">
                      {cat.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => commit(necessaryOnlyConsent())}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reject all
              </button>
              <button
                onClick={() => commit(allConsentGranted())}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Accept all
              </button>
              <button
                onClick={() => commit({ ...selection, necessary: true })}
                className="rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Save preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
